// ApiServiceAdmin.js - Service API pour le tableau de bord administrateur
import { ENV_CONFIG } from '../config/environment';
import { ApiServiceShared } from './ApiServiceShared';
import { dateStrInTz, formatTimeInTz, getAppTimezone } from '../utils/dateTime';

// Configuration pour activer/désactiver les logs de debug
const DEBUG_LOGS = false;
const debugLog = (...args) => {
  if (DEBUG_LOGS) console.log(...args);
};

export class ApiServiceAdmin {
  constructor() {
    this.baseUrl = ENV_CONFIG.apiBaseUrl;
    this.token = null;
    this.authErrorHandler = null; // Handler pour les erreurs d'authentification
    /** @type {{ id?: number, label?: string } | null} */
    this._financeSchoolYear = null;
  }

  setFinanceSchoolYear(year) {
    this._financeSchoolYear = year;
  }

  _appendFinanceYearToSearchParams(queryParams) {
    if (!this._financeSchoolYear) return;
    if (queryParams.has('school_year_id') || queryParams.has('academic_year')) return;
    if (this._financeSchoolYear.id != null) {
      queryParams.append('school_year_id', String(this._financeSchoolYear.id));
    } else if (this._financeSchoolYear.label) {
      queryParams.append('academic_year', this._financeSchoolYear.label);
    }
  }

  _withFinanceUrl(path) {
    const params = new URLSearchParams();
    const qIndex = path.indexOf('?');
    if (qIndex >= 0) {
      new URLSearchParams(path.slice(qIndex + 1)).forEach((v, k) => params.set(k, v));
      path = path.slice(0, qIndex);
    }
    this._appendFinanceYearToSearchParams(params);
    const qs = params.toString();
    return qs ? `${path}?${qs}` : path;
  }

  _withFinanceBody(data) {
    if (!data || typeof data !== 'object' || !this._financeSchoolYear?.id) return data;
    if (data.school_year_id != null) return data;
    return { ...data, school_year_id: this._financeSchoolYear.id };
  }

  static getInstance() {
    if (!ApiServiceAdmin.instance) {
      ApiServiceAdmin.instance = new ApiServiceAdmin();
      // Initialiser le token depuis localStorage si disponible
      const storedToken = localStorage.getItem('app_token');
      if (storedToken) {
        ApiServiceAdmin.instance.token = storedToken;
      }
    }
    return ApiServiceAdmin.instance;
  }

  // Méthode pour définir le handler d'erreur d'authentification
  setAuthErrorHandler(handler) {
    this.authErrorHandler = handler;
  }

  setAuthToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('app_token', token);
    }
  }

  // Méthode utilitaire pour récupérer le token depuis localStorage
  _ensureToken() {
    // Toujours vérifier localStorage au cas où le token aurait été mis à jour ailleurs
    if (!this.token) {
      this.token = localStorage.getItem('app_token');
    } else {
      // Vérifier que le token dans localStorage correspond (au cas où il aurait été mis à jour)
      const storedToken = localStorage.getItem('app_token');
      if (storedToken && storedToken !== this.token) {
        this.token = storedToken;
      }
    }
    
    if (!this.token) {
      throw new Error('Token d\'authentification manquant. Veuillez vous reconnecter.');
    }
    
    return this.token;
  }

  // Méthode pour gérer les erreurs d'authentification et rediriger vers la page d'accueil
  _handleAuthError(response, errorMessage) {
    // Vérifier si c'est une erreur 401 (Token d'accès requis) ou 403 (Token invalide)
    if (response && (response.status === 401 || response.status === 403)) {
      console.error(`❌ Erreur d'authentification (${response.status}): ${errorMessage || 'Token invalide ou expiré'}`);
      
      // Nettoyer le localStorage
      localStorage.removeItem('app_token');
      localStorage.removeItem('app_user');
      localStorage.removeItem('login_timestamp');
      
      // Utiliser le handler personnalisé si disponible, sinon fallback sur reload
      if (this.authErrorHandler && typeof this.authErrorHandler === 'function') {
        this.authErrorHandler();
      } else {
        // Rediriger vers la page de login
        window.location.href = '/';
      }
      
      return true; // Indique qu'une redirection a été effectuée
    }
    return false; // Pas d'erreur d'authentification
  }

  getCurrentUser() {
    try {
      const storedUser = localStorage.getItem('app_user');
      return storedUser ? JSON.parse(storedUser) : null;
    } catch {
      return null;
    }
  }

  async login(username, password, userType) {
    try {
      const requestData = { username, password, user_type: userType };
      const route = `${this.baseUrl}/api/auth/login`;
      
      debugLog('🔍 ADMIN API - login - Début');
      debugLog('🔍 ADMIN API - login - Username:', username);
      debugLog('🔍 ADMIN API - login - UserType:', userType);
      debugLog('🔍 ADMIN API - login - Route:', route);

      const response = await fetch(route, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erreur de connexion');
      }

      // Stocker le token et les informations utilisateur avec timestamp pour expiration de 1 heure
      this.token = data.token;
      localStorage.setItem('app_token', data.token);
      localStorage.setItem('app_user', JSON.stringify(data.user));
      localStorage.setItem('login_timestamp', Date.now().toString());
      
      debugLog('✅ Connexion réussie');

      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('❌ ADMIN API - login - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur de connexion'
      };
    }
  }

  logout() {
    this.token = null;
    localStorage.removeItem('app_token');
    localStorage.removeItem('app_user');
    localStorage.removeItem('login_timestamp');
  }

  // ==================== ADMIN DASHBOARD ====================

  async getDashboardStats() {
    try {
      this._ensureToken();
      const route = `${this.baseUrl}/api/dashboard/data`;
      
      debugLog('🔍 getDashboardStats - Début');
      
      const response = await fetch(route, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      const data = await response.json();

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      debugLog('✅ getDashboardStats - Succès');
      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      console.error('❌ getDashboardStats erreur:', error.message || error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des statistiques'
      };
    }
  }

  // ==================== STUDENTS MANAGEMENT ====================

  async getStudents(filters = {}) {
    try {
      this._ensureToken();
      const queryParams = new URLSearchParams();
      if (filters.school_year_id != null && filters.school_year_id !== '') {
        queryParams.append('school_year_id', String(filters.school_year_id));
      }
      const qs = queryParams.toString();
      const route = `${this.baseUrl}/api/admin/students${qs ? `?${qs}` : ''}`;
      
      debugLog('🔍 getStudents - Début');
      
      const response = await fetch(route, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      const data = await response.json();

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }
      
      debugLog('✅ getStudents - Succès:', data.data?.length || 0, 'étudiants');
      
      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      console.error('❌ getStudents erreur:', error.message || error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des étudiants'
      };
    }
  }

  async createStudent(studentData) {
    try {
      this._ensureToken();
      
      // ✅ Validation frontend : parent_id est obligatoire
      if (!studentData.parent_id || studentData.parent_id <= 0) {
        throw new Error('Le parent_id est obligatoire. Un étudiant doit être attaché à un parent.');
      }
      
      const route = `${this.baseUrl}/api/admin/students`;
      
      console.log('🔍 ADMIN API - createStudent - Début');
      console.log('🔍 ADMIN API - createStudent - Route:', route);
      console.log('🔍 ADMIN API - createStudent - Token présent:', this.token ? 'Oui' : 'Non');
      console.log('🔍 ADMIN API - createStudent - Données envoyées:', studentData);

      // Récupérer le paramètre checkEmailUniquenessForUsers
      let checkEmailUniqueness = true; // Valeur par défaut
      try {
        const paramResponse = await this.getParameterByName('checkEmailUniquenessForUsers');
        if (paramResponse.success && paramResponse.data) {
          checkEmailUniqueness = paramResponse.data.value === 'TRUE' || paramResponse.data.value === 'true';
          console.log('🔍 ADMIN API - createStudent - checkEmailUniquenessForUsers:', checkEmailUniqueness);
        }
      } catch (error) {
        console.warn('⚠️ ADMIN API - createStudent - Impossible de récupérer le paramètre checkEmailUniquenessForUsers, utilisation de la valeur par défaut (true)');
      }

      // Ajouter le paramètre de validation d'unicité des emails
      const requestData = {
        ...studentData,
        checkEmailUniqueness: checkEmailUniqueness
      };

      const response = await fetch(route, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        },
        body: JSON.stringify(requestData)
      });

      console.log('🔍 ADMIN API - createStudent - Status:', response.status);
      console.log('🔍 ADMIN API - createStudent - Headers:', Object.fromEntries(response.headers.entries()));

      const data = await response.json();
      console.log('🔍 ADMIN API - createStudent - Response:', data);

      if (!response.ok) {
        console.log('❌ ADMIN API - createStudent - Erreur HTTP:', data.message || `HTTP error! status: ${response.status}`);
        // ✅ Gérer les nouveaux messages d'erreur du backend
        if (response.status === 400) {
          throw new Error(data.message || 'Erreur de validation');
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      console.log('✅ ADMIN API - createStudent - Succès');
      console.log('✅ ADMIN API - createStudent - Étudiant créé avec ID:', data.data?.id || 'N/A');

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      console.error('❌ ADMIN API - createStudent - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la création de l\'étudiant'
      };
    }
  }

  async updateStudent(studentId, studentData) {
    try {
      this._ensureToken();
      const route = `${this.baseUrl}/api/admin/students/${studentId}`;
      
      console.log('🔍 ADMIN API - updateStudent - Début');
      console.log('🔍 ADMIN API - updateStudent - Route:', route);
      console.log('🔍 ADMIN API - updateStudent - Student ID:', studentId);
      console.log('🔍 ADMIN API - updateStudent - Token présent:', this.token ? 'Oui' : 'Non');
      console.log('🔍 ADMIN API - updateStudent - Données envoyées:', studentData);
      
      const response = await fetch(route, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        },
        body: JSON.stringify(studentData)
      });

      console.log('🔍 ADMIN API - updateStudent - Status:', response.status);
      console.log('🔍 ADMIN API - updateStudent - Headers:', Object.fromEntries(response.headers.entries()));

      const data = await response.json();
      console.log('🔍 ADMIN API - updateStudent - Response:', data);

      if (!response.ok) {
        console.log('❌ ADMIN API - updateStudent - Erreur HTTP:', data.message || `HTTP error! status: ${response.status}`);
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      console.log('✅ ADMIN API - updateStudent - Succès');
      console.log('✅ ADMIN API - updateStudent - Étudiant mis à jour avec ID:', studentId);

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      console.error('❌ ADMIN API - updateStudent - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la modification de l\'étudiant'
      };
    }
  }

  async checkStudentDeletion(studentId) {
    try {
      this._ensureToken();
      const response = await fetch(`${this.baseUrl}/api/admin/students/${studentId}/deletion-check`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });
      const data = await response.json();
      if (!response.ok) {
        return { success: false, error: data.message || `HTTP ${response.status}` };
      }
      return { success: true, data: data.data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erreur checkStudentDeletion' };
    }
  }

  async deleteStudent(studentId) {
    try {
      this._ensureToken();
      const route = `${this.baseUrl}/api/admin/students/${studentId}`;
      
      console.log('🔍 ADMIN API - deleteStudent - Début');
      console.log('🔍 ADMIN API - deleteStudent - Route:', route);
      console.log('🔍 ADMIN API - deleteStudent - Student ID:', studentId);
      console.log('🔍 ADMIN API - deleteStudent - Token présent:', this.token ? 'Oui' : 'Non');
      
      const response = await fetch(route, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      console.log('🔍 ADMIN API - deleteStudent - Status:', response.status);
      console.log('🔍 ADMIN API - deleteStudent - Headers:', Object.fromEntries(response.headers.entries()));

      const data = await response.json();
      console.log('🔍 ADMIN API - deleteStudent - Response:', data);

      if (!response.ok) {
        console.log('❌ ADMIN API - deleteStudent - Erreur HTTP:', data.message || `HTTP error! status: ${response.status}`);
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      console.log('✅ ADMIN API - deleteStudent - Succès');
      console.log('✅ ADMIN API - deleteStudent - Étudiant supprimé avec ID:', studentId);

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      console.error('❌ ADMIN API - deleteStudent - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la suppression de l\'étudiant'
      };
    }
  }

  // ==================== TEACHERS MANAGEMENT ====================

  async getTeachers() {
    try {
      this._ensureToken();
      debugLog('🔍 getTeachers - Début');
      
      // Vérifier que l'URL de base est définie
      if (!this.baseUrl) {
        throw new Error('URL de base non définie');
      }
      
      // Vérifier que l'utilisateur est connecté
      if (!this.token) {
        throw new Error('Token d\'authentification manquant. Veuillez vous reconnecter.');
      }

      // Utiliser directement la route standard (la route avec spécialisations n'existe pas encore)
      const url = `${this.baseUrl}/api/admin/teachers`;
      debugLog('🔍 getTeachers - URL:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      const data = await response.json();
      debugLog('✅ getTeachers - Enseignants:', data.data?.length || 0);

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des enseignants'
      };
    }
  }

  async createTeacher(teacherData) {
    try {
      this._ensureToken();
      console.log('🔍 ApiService - token:', this.token ? 'Présent' : 'Absent');

      const url = `${this.baseUrl}/api/admin/teachers`;
      console.log('🔍 ApiService - URL complète:', url);

      // Récupérer le paramètre checkEmailUniquenessForUsers
      let checkEmailUniqueness = true; // Valeur par défaut
      try {
        const paramResponse = await this.getParameterByName('checkEmailUniquenessForUsers');
        if (paramResponse.success && paramResponse.data) {
          checkEmailUniqueness = paramResponse.data.value === 'TRUE' || paramResponse.data.value === 'true';
          console.log('🔍 ApiService - createTeacher - checkEmailUniquenessForUsers:', checkEmailUniqueness);
        }
      } catch (error) {
        console.warn('⚠️ ApiService - createTeacher - Impossible de récupérer le paramètre checkEmailUniquenessForUsers, utilisation de la valeur par défaut (true)');
      }

      // Ajouter le paramètre de validation d'unicité des emails
      const requestData = {
        ...teacherData,
        checkEmailUniqueness: checkEmailUniqueness
      };

      const requestOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        },
        body: JSON.stringify(requestData)
      };
      
      console.log('🔍 ApiService - Options de requête:', requestOptions);

      const response = await fetch(url, requestOptions);
      const data = await response.json();

      console.log('🔍 ApiService - Status:', response.status);
      console.log('🔍 ApiService - Response:', data);

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la création de l\'enseignant'
      };
    }
  }

  async updateTeacher(teacherId, teacherData) {
    try {
      this._ensureToken();
      console.log('🔍 ApiService - token:', this.token ? 'Présent' : 'Absent');

      const url = `${this.baseUrl}/api/admin/teachers/${teacherId}`;
      console.log('🔍 ApiService - URL complète:', url);

      // Préparer les données selon ce que le backend attend
      const backendData = {
        fullName: teacherData.fullName,
        username: teacherData.username,
        phone: teacherData.phone,
        email: teacherData.email || null,
        specialization: teacherData.specialization,
        assigned_halqas: teacherData.assigned_halqas || '',
        experience_years: teacherData.experience_years || 0,
        certification_date: teacherData.certification_date || null,
        salary: teacherData.salary || 0,
        notes: teacherData.notes || '',
        gender: teacherData.gender || 'Male'
      };

      // Ajouter le mot de passe seulement s'il est fourni
      if (teacherData.password && teacherData.password.trim() !== '') {
        backendData.password = teacherData.password;
      }

      console.log('🔍 ApiService - Données envoyées au backend:', backendData);

      const requestOptions = {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        },
        body: JSON.stringify(backendData)
      };

      const response = await fetch(url, requestOptions);
      const data = await response.json();

      console.log('🔍 ApiService - Status:', response.status);
      console.log('🔍 ApiService - Response:', data);

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la modification de l\'enseignant'
      };
    }
  }

  async deleteTeacher(teacherId) {
    try {
      this._ensureToken();
      
      const url = `${this.baseUrl}/api/admin/teachers/${teacherId}`;

      const requestOptions = {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      };

      const response = await fetch(url, requestOptions);
      const data = await response.json();

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la suppression de l\'enseignant'
      };
    }
  }

  async toggleTeacherStatus(teacherId) {
    try {
      this._ensureToken();
      
      const url = `${this.baseUrl}/api/admin/teachers/${teacherId}/toggle-status`;

      const requestOptions = {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      };

      const response = await fetch(url, requestOptions);
      const data = await response.json();

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors du basculement du statut'
      };
    }
  }

  // ==================== PARENTS MANAGEMENT ====================
  
  async getParents() {
    try {
      this._ensureToken();
      
      console.log('🔍 API DEBUG - getParents - Début');
      console.log('🔍 API DEBUG - getParents - URL:', `${this.baseUrl}/api/admin/parents`);
      
      const response = await fetch(`${this.baseUrl}/api/admin/parents`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      const data = await response.json();

      console.log('🔍 API DEBUG - getParents - Status:', response.status);
      console.log('🔍 API DEBUG - getParents - Response:', data);

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des parents'
      };
    }
  }

  async addParent(parentData) {
    try {
      this._ensureToken();
      
      console.log('🔍 FRONTEND - addParent - Début de la fonction');
      console.log('🔍 FRONTEND - addParent - Données reçues:', parentData);
      console.log('🔍 FRONTEND - addParent - Type de données:', typeof parentData);
      console.log('🔍 FRONTEND - addParent - Clés disponibles:', Object.keys(parentData));
      
      // Récupérer le paramètre checkEmailUniquenessForUsers
      let checkEmailUniqueness = true; // Valeur par défaut
      try {
        const paramResponse = await this.getParameterByName('checkEmailUniquenessForUsers');
        if (paramResponse.success && paramResponse.data) {
          checkEmailUniqueness = paramResponse.data.value === 'TRUE' || paramResponse.data.value === 'true';
          console.log('🔍 FRONTEND - addParent - checkEmailUniquenessForUsers:', checkEmailUniqueness);
        }
      } catch (error) {
        console.warn('⚠️ FRONTEND - addParent - Impossible de récupérer le paramètre checkEmailUniquenessForUsers, utilisation de la valeur par défaut (true)');
      }
      
      // Utiliser la route spécialisée pour les parents avec support des relations étudiants
      const userData = {
        fullName: parentData.fullName,
        username: parentData.username,
        password: parentData.password,
        phone: parentData.phone,
        email: parentData.email,
        address: parentData.address,
        emergency_contact: parentData.emergency_contact,
        notes: parentData.notes,
        type: 'parent', // Type d'utilisateur
        gender: parentData.gender || 'Male',
        // Support pour les relations avec étudiants (nouveau selon FRONTEND_INTEGRATION.md)
        student_ids: parentData.student_ids || [],
        relationships: parentData.relationships || [],
        // Ajouter le paramètre de validation d'unicité des emails
        checkEmailUniqueness: checkEmailUniqueness
      };

      console.log('🔍 FRONTEND - addParent - Données utilisateur préparées:', userData);
      console.log('🔍 FRONTEND - addParent - URL:', `${this.baseUrl}/api/admin/users`);

      const response = await fetch(`${this.baseUrl}/api/admin/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        },
        body: JSON.stringify(userData)
      });

      const data = await response.json();
      
      console.log('🔍 FRONTEND - addParent - Status:', response.status);
      console.log('🔍 FRONTEND - addParent - Response:', data);

      if (!response.ok) {
        throw new Error(data.message || data.error || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de l\'ajout du parent'
      };
    }
  }

  async updateParent(parentId, parentData) {
    try {
      this._ensureToken();
      
      console.log('🔍 API DEBUG - updateParent - Parent ID:', parentId);
      console.log('🔍 API DEBUG - updateParent - Données reçues:', parentData);
      
      // Utiliser la route spécialisée pour les parents avec support des relations étudiants
      const updateData = {
        fullName: parentData.fullName,
        username: parentData.username,
        phone: parentData.phone,
        email: parentData.email,
        address: parentData.address,
        emergency_contact: parentData.emergency_contact,
        notes: parentData.notes,
        gender: parentData.gender || 'Male',
        // Support pour les relations avec étudiants (nouveau selon FRONTEND_INTEGRATION.md)
        student_ids: parentData.student_ids,
        remove_student_ids: parentData.remove_student_ids,
        relationships: parentData.relationships
      };

      // Inclure le mot de passe uniquement s'il est fourni et non vide
      if (parentData.password && parentData.password.trim() !== '') {
        updateData.password = parentData.password;
      }

      console.log('🔍 API DEBUG - updateParent - Données préparées:', updateData);
      console.log('🔍 API DEBUG - updateParent - Route:', `${this.baseUrl}/api/admin/parents/${parentId}`);

      const response = await fetch(`${this.baseUrl}/api/admin/parents/${parentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        },
        body: JSON.stringify(updateData)
      });

      const data = await response.json();
      
      console.log('🔍 API DEBUG - updateParent - Status:', response.status);
      console.log('🔍 API DEBUG - updateParent - Response:', data);

      if (!response.ok) {
        throw new Error(data.message || data.error || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la modification du parent'
      };
    }
  }

  async createUnifiedUser(payload) {
    try {
      this._ensureToken();
      const response = await fetch(`${this.baseUrl}/api/admin/user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (!response.ok) {
        return { success: false, error: data.message || data.error || `HTTP ${response.status}` };
      }
      return { success: true, data: data.data || data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erreur createUnifiedUser' };
    }
  }

  async updateUnifiedUser(id, payload) {
    try {
      this._ensureToken();
      const response = await fetch(`${this.baseUrl}/api/admin/user/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (!response.ok) {
        return { success: false, error: data.message || data.error || `HTTP ${response.status}` };
      }
      return { success: true, data: data.data || data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erreur updateUnifiedUser' };
    }
  }

  async deleteParent(parentId) {
    try {
      this._ensureToken();
      const url = `${this.baseUrl}/api/admin/parents/${parentId}`;

      const requestOptions = {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      };

      const response = await fetch(url, requestOptions);
      const data = await response.json();

      if (!response.ok) {
        // ✅ NOUVEAU : Gérer l'erreur si des étudiants sont référencés
        if (response.status === 400 && data.students && Array.isArray(data.students)) {
          // Le backend retourne déjà le message avec la liste des étudiants
          // Utiliser le message du backend directement
          const errorMessage = data.message || `Impossible de supprimer ce parent. ${data.students.length} étudiant(s) lui sont attaché(s)`;
          const error = new Error(errorMessage);
          error.students = data.students; // Stocker les étudiants pour utilisation ultérieure
          throw error;
        }
        throw new Error(data.message || data.error || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la suppression du parent',
        students: error.students || null // Inclure les étudiants si disponibles
      };
    }
  }

  async toggleParentStatus(parentId) {
    try {
      this._ensureToken();
      
      console.log('🔍 FRONTEND - toggleParentStatus - Parent ID:', parentId);
      console.log('🔍 FRONTEND - toggleParentStatus - URL:', `${this.baseUrl}/api/admin/parents/${parentId}/toggle-status`);

      const response = await fetch(`${this.baseUrl}/api/admin/parents/${parentId}/toggle-status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      console.log('🔍 FRONTEND - toggleParentStatus - Status:', response.status);
      const data = await response.json();
      console.log('🔍 FRONTEND - toggleParentStatus - Réponse:', data);

      if (!response.ok) {
        throw new Error(data.message || data.error || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors du basculement du statut'
      };
    }
  }

  // ==================== ADMINS MANAGEMENT ====================
  
  async getAdmins() {
    try {
      this._ensureToken();
      
      console.log('🔍 API DEBUG - getAdmins - Route:', `${this.baseUrl}/api/admin/admins`);
      
      const response = await fetch(`${this.baseUrl}/api/admin/admins`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      const data = await response.json();

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des administrateurs'
      };
    }
  }

  async addAdmin(adminData) {
    try {
      this._ensureToken();
      
      console.log('🔍 API DEBUG - addAdmin - Données envoyées:', adminData);
      console.log('🔍 API DEBUG - addAdmin - Route:', `${this.baseUrl}/api/admin/admins`);
      
      // Récupérer le paramètre checkEmailUniquenessForUsers
      let checkEmailUniqueness = true; // Valeur par défaut
      try {
        const paramResponse = await this.getParameterByName('checkEmailUniquenessForUsers');
        if (paramResponse.success && paramResponse.data) {
          checkEmailUniqueness = paramResponse.data.value === 'TRUE' || paramResponse.data.value === 'true';
          console.log('🔍 API DEBUG - addAdmin - checkEmailUniquenessForUsers:', checkEmailUniqueness);
        }
      } catch (error) {
        console.warn('⚠️ API DEBUG - addAdmin - Impossible de récupérer le paramètre checkEmailUniquenessForUsers, utilisation de la valeur par défaut (true)');
      }
      
      // Ajouter le paramètre de validation d'unicité des emails
      const requestData = {
        ...adminData,
        checkEmailUniqueness: checkEmailUniqueness
      };
      
      console.log('🔍 API DEBUG - addAdmin - Données complètes avec checkEmailUniqueness:', requestData);
      
      const response = await fetch(`${this.baseUrl}/api/admin/admins`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        },
        body: JSON.stringify(requestData)
      });

      const data = await response.json();
      
      console.log('🔍 API DEBUG - addAdmin - Status:', response.status);
      console.log('🔍 API DEBUG - addAdmin - Réponse complète:', JSON.stringify(data, null, 2));
      console.log('🔍 API DEBUG - addAdmin - Données envoyées (pour référence):', JSON.stringify(adminData, null, 2));

      if (!response.ok) {
        let errorMessage = data.message || data.error || `HTTP error! status: ${response.status}`;
        
        // Améliorer le message d'erreur pour les conflits d'unicité
        // Note: Les erreurs d'email/téléphone en doublon ne sont pas traitées comme des erreurs spécifiques
        // car un utilisateur peut être admin, parent et teacher simultanément
        if (errorMessage.includes('existe déjà') || errorMessage.includes('already exists') || errorMessage.includes('duplicate')) {
          // Essayer d'identifier quel champ cause le problème depuis les détails du backend
          console.log('🔍 API DEBUG - addAdmin - Détails de l\'erreur:', data);
          
          // Ne pas traiter les erreurs d'email ou téléphone comme des erreurs spécifiques
          // Un utilisateur peut avoir le même email/téléphone pour différents rôles
          if (data.field === 'email' || data.field === 'phone' || 
              errorMessage.toLowerCase().includes('email') || errorMessage.includes('téléphone') || 
              errorMessage.toLowerCase().includes('phone') || errorMessage.includes('بريد') || 
              errorMessage.includes('هاتف')) {
            // Message générique sans mentionner l'email/téléphone
            console.log('⚠️ API DEBUG - addAdmin - Erreur d\'email/téléphone détectée mais traitée comme erreur générique (utilisateur multi-rôles autorisé)');
            errorMessage = `خطأ في إضافة المدير. يرجى المحاولة مرة أخرى أو التحقق من البيانات المدخلة.`;
          } else if (data.field === 'username') {
            // Ne traiter que le username comme erreur spécifique
            errorMessage = 'اسم المستخدم موجود بالفعل. يرجى اختيار اسم مستخدم آخر.';
          } else if (data.details) {
            errorMessage = `خطأ: ${data.details}`;
          } else if (errorMessage.includes('username') || errorMessage.includes('nom d\'utilisateur')) {
            errorMessage = 'اسم المستخدم موجود بالفعل. يرجى اختيار اسم مستخدم آخر.';
          } else {
            // Message générique sans mentionner l'email/téléphone
            errorMessage = `خطأ في إضافة المدير: ${errorMessage}\n\nيرجى التحقق من البيانات المدخلة.`;
          }
        }
        
        throw new Error(errorMessage);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      console.error('❌ API DEBUG - addAdmin - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de l\'ajout de l\'administrateur'
      };
    }
  }

  async updateAdmin(adminId, adminData) {
    try {
      this._ensureToken();
      
      console.log('🔍 API DEBUG - updateAdmin - ID:', adminId);
      console.log('🔍 API DEBUG - updateAdmin - Données envoyées:', adminData);
      console.log('🔍 API DEBUG - updateAdmin - Route:', `${this.baseUrl}/api/admin/admins/${adminId}`);
      
      const response = await fetch(`${this.baseUrl}/api/admin/admins/${adminId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        },
        body: JSON.stringify(adminData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la modification de l\'administrateur'
      };
    }
  }

  async deleteAdmin(adminId) {
    try {
      this._ensureToken();
      
      console.log('🔍 API DEBUG - deleteAdmin - ID:', adminId);
      console.log('🔍 API DEBUG - deleteAdmin - Route:', `${this.baseUrl}/api/admin/admins/${adminId}`);
      
      const response = await fetch(`${this.baseUrl}/api/admin/admins/${adminId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      const data = await response.json();

      console.log('🔍 API DEBUG - deleteAdmin - Status:', response.status);
      console.log('🔍 API DEBUG - deleteAdmin - Réponse:', data);

      if (!response.ok) {
        throw new Error(data.message || data.error || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      console.error('❌ API DEBUG - deleteAdmin - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la suppression de l\'administrateur'
      };
    }
  }

  async toggleAdminStatus(adminId) {
    try {
      this._ensureToken();
      
      console.log('🔍 API DEBUG - toggleAdminStatus - ID:', adminId);
      console.log('🔍 API DEBUG - toggleAdminStatus - Route:', `${this.baseUrl}/api/admin/admins/${adminId}/toggle-status`);
      
      const response = await fetch(`${this.baseUrl}/api/admin/admins/${adminId}/toggle-status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors du basculement du statut'
      };
    }
  }

  // ==================== PARENT-STUDENT RELATIONS ====================
  
  // Récupérer toutes les relations parent-étudiant
  async getParentStudentRelations() {
    try {
      this._ensureToken();
      const response = await fetch(`${this.baseUrl}/api/admin/parent-students`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      const data = await response.json();

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des relations parent-étudiant'
      };
    }
  }

  // Récupérer un parent par ID
  async getParentById(parentId) {
    try {
      this._ensureToken();
      const response = await fetch(`${this.baseUrl}/api/admin/parents/${parentId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      const data = await response.json();

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération du parent'
      };
    }
  }

  // Récupérer les étudiants d'un parent
  async getParentStudents(parentId) {
    try {
      this._ensureToken();
      const response = await fetch(`${this.baseUrl}/api/admin/parents/${parentId}/students`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des étudiants du parent'
      };
    }
  }

  async reenrollParentStudents(parentId, data) {
    return this.makeRequest(`/api/admin/parents/${parentId}/re-enroll`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Récupérer les parents d'un étudiant
  async getStudentParents(studentId) {
    try {
      this._ensureToken();
      const response = await fetch(`${this.baseUrl}/api/admin/parent-students/student/${studentId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      const data = await response.json();

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des parents de l\'étudiant'
      };
    }
  }

  // Ajouter une relation parent-étudiant (support relations unique ou multiples)
  async addParentStudentRelation(relationData) {
    try {
      this._ensureToken();
      const url = `${this.baseUrl}/api/admin/parent-students`;
      
      // Support pour relation unique (parent_id + student_id) ou multiples (parent_id + student_ids)
      // Selon FRONTEND_INTEGRATION.md
      console.log('🔍 API - addParentStudentRelation - Données:', relationData);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        },
        body: JSON.stringify(relationData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      // Si relations multiples, data peut contenir data.data (array) et data.errors
      return {
        success: true,
        data: data.data || data,
        errors: data.errors || null
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de l\'ajout de la relation parent-étudiant'
      };
    }
  }

  // Mettre à jour une relation parent-étudiant
  async updateParentStudentRelation(relationId, updateData) {
    try {
      this._ensureToken();
      const url = `${this.baseUrl}/api/admin/parent-students/${relationId}`;
      
      console.log('🔍 API - updateParentStudentRelation - ID:', relationId);
      console.log('🔍 API - updateParentStudentRelation - Données:', updateData);
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        },
        body: JSON.stringify(updateData)
      });

      const data = await response.json();

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la mise à jour de la relation parent-étudiant'
      };
    }
  }

  // Supprimer une relation parent-étudiant
  async deleteParentStudentRelation(relationId) {
    try {
      this._ensureToken();
      const url = `${this.baseUrl}/api/admin/parent-students/${relationId}`;
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      const data = await response.json();

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la suppression de la relation parent-étudiant'
      };
    }
  }

  // ==================== STUDENT-TEACHERS MANAGEMENT ====================

  // Récupérer toutes les relations étudiant-enseignant
  async getStudentTeachers() {
    try {
      this._ensureToken();
      const url = `${this.baseUrl}/api/admin/student-teachers`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      const data = await response.json();

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des relations étudiant-enseignant'
      };
    }
  }

  // Récupérer les enseignants d'un étudiant
  async getStudentTeachersByStudent(studentId) {
    try {
      this._ensureToken();
      const url = `${this.baseUrl}/api/admin/student-teachers/student/${studentId}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      const data = await response.json();

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des enseignants de l\'étudiant'
      };
    }
  }

  // Récupérer les étudiants d'un enseignant
  async getStudentTeachersByTeacher(teacherId) {
    try {
      this._ensureToken();
      const url = `${this.baseUrl}/api/admin/student-teachers/teacher/${teacherId}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      const data = await response.json();

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des étudiants de l\'enseignant'
      };
    }
  }

  // Ajouter une relation étudiant-enseignant
  async addStudentTeacherRelation(relationData) {
    try {
      this._ensureToken();
      const url = `${this.baseUrl}/api/admin/student-teachers`;
      
      console.log('🔍 API - addStudentTeacherRelation - Données:', relationData);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        },
        body: JSON.stringify(relationData)
      });

      const data = await response.json();

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de l\'ajout de la relation étudiant-enseignant'
      };
    }
  }

  // Mettre à jour une relation étudiant-enseignant
  async updateStudentTeacherRelation(relationId, updateData) {
    try {
      this._ensureToken();
      const url = `${this.baseUrl}/api/admin/student-teachers/${relationId}`;
      
      console.log('🔍 API - updateStudentTeacherRelation - ID:', relationId);
      console.log('🔍 API - updateStudentTeacherRelation - Données:', updateData);
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        },
        body: JSON.stringify(updateData)
      });

      const data = await response.json();

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la mise à jour de la relation étudiant-enseignant'
      };
    }
  }

  // Supprimer une relation étudiant-enseignant
  async deleteStudentTeacherRelation(relationId) {
    try {
      this._ensureToken();
      const url = `${this.baseUrl}/api/admin/student-teachers/${relationId}`;
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      const data = await response.json();

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la suppression de la relation étudiant-enseignant'
      };
    }
  }

  // ==================== GRADES MANAGEMENT ====================

  async getGrades(locale = 'ar', schoolYearId) {
    try {
      this._ensureToken();
      const lang = locale === 'fr' || locale === 'en' ? locale : 'ar';
      const params = new URLSearchParams({ lang });
      if (schoolYearId != null) params.set('school_year_id', String(schoolYearId));
      const response = await fetch(`${this.baseUrl}/api/admin/grades?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      const data = await response.json();

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des notes'
      };
    }
  }

  async createGrade(gradeData, locale = 'ar') {
    try {
      this._ensureToken();
      const lang = locale === 'fr' || locale === 'en' ? locale : 'ar';
      const response = await fetch(`${this.baseUrl}/api/admin/grades?lang=${encodeURIComponent(lang)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        },
        body: JSON.stringify(gradeData)
      });

      const data = await response.json();

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la création de la note'
      };
    }
  }

  async deleteGrade(gradeId) {
    try {
      this._ensureToken();
      
      const response = await fetch(`${this.baseUrl}/api/admin/grades/${gradeId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      const data = await response.json();

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la suppression de la note'
      };
    }
  }

  // ==================== DASHBOARD DATA ====================

  async getDashboardData(schoolYearId) {
    try {
      this._ensureToken();
      const url = new URL(`${this.baseUrl}/api/dashboard/data`);
      if (schoolYearId != null) url.searchParams.set('school_year_id', String(schoolYearId));
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      const data = await response.json();

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des données du dashboard'
      };
    }
  }

  // ==================== SUBJECTS MANAGEMENT ====================

  async getSubjects(locale = 'ar') {
    try {
      this._ensureToken();
      const lang = locale === 'fr' || locale === 'en' ? locale : 'ar';
      const route = `${this.baseUrl}/api/admin/subjects?locale=${encodeURIComponent(lang)}`;
      
      console.log('🔍 ADMIN API - getSubjects - Début');
      console.log('🔍 ADMIN API - getSubjects - Route:', route);
      console.log('🔍 ADMIN API - getSubjects - Token présent:', this.token ? 'Oui' : 'Non');
      
      const response = await fetch(route, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      console.log('🔍 ADMIN API - getSubjects - Status:', response.status);
      console.log('🔍 ADMIN API - getSubjects - Headers:', Object.fromEntries(response.headers.entries()));

      const data = await response.json();
      console.log('🔍 ADMIN API - getSubjects - Response:', data);

      if (!response.ok) {
        console.log('❌ ADMIN API - getSubjects - Erreur HTTP:', data.message || `HTTP error! status: ${response.status}`);
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      console.log('✅ ADMIN API - getSubjects - Succès');
      console.log('✅ ADMIN API - getSubjects - Nombre de matières:', data.data?.length || 0);

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      console.error('❌ ADMIN API - getSubjects - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des matières'
      };
    }
  }

  async createSubject(labels, status = 'نشط') {
    try {
      this._ensureToken();
      const response = await fetch(`${this.baseUrl}/api/admin/subjects`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        },
        body: JSON.stringify({ labels, status })
      });
      const data = await response.json();
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return { success: false, error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.' };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }
      return { success: true, data: data.data || data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la création de la matière'
      };
    }
  }

  async updateSubject(id, labels, status) {
    try {
      this._ensureToken();
      const body = { labels };
      if (status !== undefined && status !== null) body.status = status;
      const response = await fetch(`${this.baseUrl}/api/admin/subjects/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        },
        body: JSON.stringify(body)
      });
      const data = await response.json();
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return { success: false, error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.' };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }
      return { success: true, data: data.data || data, message: data.message };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la mise à jour de la matière'
      };
    }
  }

  async getTeacherSpecializations() {
    try {
      this._ensureToken();
      
      const response = await fetch(`${this.baseUrl}/api/admin/teacher-specializations`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      const data = await response.json();

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des spécialisations'
      };
    }
  }

  async createTeacherSpecialization(teacherId, specializationData) {
    try {
      this._ensureToken();
      const route = `${this.baseUrl}/api/admin/teacher-specializations`;
      // Backend expects teacher_id (not teacherId or user_id)
      const requestData = { teacher_id: teacherId, ...specializationData };
      
      console.log('🔍 ADMIN API - createTeacherSpecialization - Début');
      console.log('🔍 ADMIN API - createTeacherSpecialization - Route:', route);
      console.log('🔍 ADMIN API - createTeacherSpecialization - Teacher ID:', teacherId);
      console.log('🔍 ADMIN API - createTeacherSpecialization - Token présent:', this.token ? 'Oui' : 'Non');
      console.log('🔍 ADMIN API - createTeacherSpecialization - Données envoyées:', requestData);
      
      const response = await fetch(route, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        },
        body: JSON.stringify(requestData)
      });

      console.log('🔍 ADMIN API - createTeacherSpecialization - Status:', response.status);
      console.log('🔍 ADMIN API - createTeacherSpecialization - Headers:', Object.fromEntries(response.headers.entries()));

      const data = await response.json();
      console.log('🔍 ADMIN API - createTeacherSpecialization - Response:', data);

      if (!response.ok) {
        console.log('❌ ADMIN API - createTeacherSpecialization - Erreur HTTP:', data.message || `HTTP error! status: ${response.status}`);
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      console.log('✅ ADMIN API - createTeacherSpecialization - Succès');
      console.log('✅ ADMIN API - createTeacherSpecialization - Spécialisation créée pour Teacher ID:', teacherId);

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      console.error('❌ ADMIN API - createTeacherSpecialization - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la création de la spécialisation'
      };
    }
  }

  // ==================== USERS MANAGEMENT ====================

  async getUsers() {
    try {
      this._ensureToken();
      
      const response = await fetch(`${this.baseUrl}/api/admin/users`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      const data = await response.json();

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des utilisateurs'
      };
    }
  }

  // ==================== ATTENDANCE ====================
  
  // Récupérer les statistiques de présence/absence
  async getAttendance(filters = {}) {
    try {
      this._ensureToken();
      
      // Construire les paramètres de requête
      const queryParams = new URLSearchParams();
      
      // Ajouter les filtres optionnels (teacherId n'est pas forcé pour l'admin)
      if (filters.teacherId) {
        queryParams.append('teacherId', filters.teacherId.toString());
      }
      if (filters.studentId) {
        queryParams.append('studentId', filters.studentId.toString());
      }
      if (filters.startDate) {
        queryParams.append('startDate', filters.startDate);
      }
      if (filters.endDate) {
        queryParams.append('endDate', filters.endDate);
      }
      // Année scolaire consultée (restreint le périmètre des élèves via student_enrollments)
      if (filters.school_year_id != null && filters.school_year_id !== '') {
        queryParams.append('school_year_id', String(filters.school_year_id));
      }

      const queryString = queryParams.toString();
      const url = `${this.baseUrl}/api/quran-records/attendance${queryString ? `?${queryString}` : ''}`;

      console.log('🔍 ADMIN API - getAttendance - Début de l\'appel');
      console.log('🔍 ADMIN API - getAttendance - Filtres reçus:', JSON.stringify(filters, null, 2));
      console.log('🔍 ADMIN API - getAttendance - URL complète:', url);
      console.log('🔍 ADMIN API - getAttendance - Query params:', queryString);
      console.log('🔍 ADMIN API - getAttendance - Méthode: GET');
      console.log('🔍 ADMIN API - getAttendance - Token présent:', !!this.token);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      console.log('🔍 ADMIN API - getAttendance - Status HTTP:', response.status);
      console.log('🔍 ADMIN API - getAttendance - Response OK:', response.ok);

      const data = await response.json();
      console.log('🔍 ADMIN API - getAttendance - Réponse brute:', JSON.stringify(data, null, 2));

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        const errorMessage = data.message || data.error || `HTTP error! status: ${response.status}`;
        console.error('❌ ADMIN API - getAttendance - Erreur HTTP:', {
          status: response.status,
          statusText: response.statusText,
          message: errorMessage,
          data: data
        });
        throw new Error(errorMessage);
      }

      // Vérifier la structure de la réponse
      const responseData = data.data || data;
      console.log('✅ ADMIN API - getAttendance - Données extraites:', JSON.stringify(responseData, null, 2));
      console.log('✅ ADMIN API - getAttendance - present:', responseData?.present);
      console.log('✅ ADMIN API - getAttendance - absent:', responseData?.absent);
      console.log('✅ ADMIN API - getAttendance - total:', responseData?.total);
      console.log('✅ ADMIN API - getAttendance - records count:', responseData?.records?.length || 0);

      return {
        success: true,
        data: responseData
      };
    } catch (error) {
      console.error('❌ ADMIN API - getAttendance - Erreur exception:', {
        message: error instanceof Error ? error.message : 'Erreur inconnue',
        error: error,
        stack: error instanceof Error ? error.stack : undefined
      });
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des présents/absents'
      };
    }
  }

  // Récupérer les présences/absences par date
  async getAttendanceByDate(filters = {}) {
    try {
      this._ensureToken();
      
      // Construire les paramètres de requête
      const queryParams = new URLSearchParams();
      
      if (filters.teacherId) {
        queryParams.append('teacherId', filters.teacherId.toString());
      }
      
      if (filters.studentId) {
        queryParams.append('studentId', filters.studentId.toString());
      }
      
      if (filters.halqa) {
        queryParams.append('halqa', filters.halqa);
      }
      
      if (filters.startDate) {
        queryParams.append('startDate', filters.startDate);
      }
      
      if (filters.endDate) {
        queryParams.append('endDate', filters.endDate);
      }

      if (filters.school_year_id != null) {
        queryParams.append('school_year_id', String(filters.school_year_id));
      }

      const queryString = queryParams.toString();
      const url = `${this.baseUrl}/api/quran-records/attendance-by-date${queryString ? `?${queryString}` : ''}`;

      debugLog('📡 getAttendanceByDate - URL:', url);
      debugLog('📡 getAttendanceByDate - Filters:', filters);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      const data = await response.json();
      
      debugLog('📥 getAttendanceByDate - Status:', response.status);
      debugLog('📥 getAttendanceByDate - Response:', {
        success: data.success,
        recordsCount: data.data?.records?.length || 0,
        hasSummary: !!data.data?.summary,
        summary: data.data?.summary,
        firstRecords: data.data?.records?.slice(0, 3)
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      debugLog('✅ getAttendanceByDate - Données complètes:', data.data);
      
      return {
        success: true,
        data: data.data
      };
    } catch (error) {
      console.error('❌ getAttendanceByDate erreur:', error.message || error);
      debugLog('❌ getAttendanceByDate - Stack:', error.stack);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des présences/absences par date'
      };
    }
  }

  // ==================== COMMUNICATION ====================

  async getMessages() {
    try {
      this._ensureToken();
      
      const url = `${this.baseUrl}/api/admin/messages`;
      const headers = {
        'Content-Type': 'application/json',
        ...(this.token && { 'Authorization': `Bearer ${this.token}` })
      };
      
      // Logs des paramètres en un seul objet JSON
      const requestParams = {
        method: 'GET',
        route: '/api/admin/messages',
        url: url,
        headers: headers,
        token_present: !!this.token,
        token_preview: this.token ? this.token.substring(0, 20) + '...' : null,
        base_url: this.baseUrl,
        parameters: null,
        note: 'GET request - aucun paramètre requis'
      };
      console.log('📥 ADMIN API - getMessages - Requête:', JSON.stringify(requestParams, null, 2));
      
      const response = await fetch(url, {
        method: 'GET',
        headers: headers
      });

      const data = await response.json();
      
      // Logs de la réponse en un seul objet JSON (commenté pour réduire les logs)
      // const responseData = {
      //   status: response.status,
      //   status_text: response.statusText,
      //   response_ok: response.ok,
      //   response_body: data,
      //   messages_count: data?.data ? (Array.isArray(data.data) ? data.data.length : 0) : 0,
      //   success: data?.success || false
      // };
      //console.log('📥 ADMIN API - getMessages - Réponse:', JSON.stringify(responseData, null, 2));

      if (!response.ok) {
        const errorData = {
          status: response.status,
          status_text: response.statusText,
          error_message: data?.message || `HTTP error! status: ${response.status}`,
          response_body: data
        };
        console.error('❌ ADMIN API - getMessages - Erreur HTTP:', JSON.stringify(errorData, null, 2));
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      // NOUVEAU SCHÉMA: Parser recipient_ids si c'est une string JSON
      // Utiliser created_date au lieu de sent_date/sent_time
      // student_gender et student_label sont maintenant inclus dans la réponse
      const messages = (data?.data || data || []).map(msg => ({
        ...msg,
        // Parser recipient_ids si c'est une string
        recipient_ids: typeof msg.recipient_ids === 'string' 
          ? (msg.recipient_ids ? JSON.parse(msg.recipient_ids) : null)
          : (msg.recipient_ids || null),
        // Utiliser created_date pour la date/heure (sent_date et sent_time n'existent plus)
        // ✅ rendues dans le fuseau de l'ASSOCIATION (`default_timezone`), pas dans celui du poste
        sent_date: msg.created_date ? dateStrInTz(new Date(msg.created_date), getAppTimezone()) : null,
        sent_time: msg.created_date ? formatTimeInTz(new Date(msg.created_date), getAppTimezone(), 'fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }) : null,
        // student_gender et student_label sont maintenant inclus dans la réponse (peuvent être null)
        student_gender: msg.student_gender || null,
        student_label: msg.student_label || null
      }));

      return {
        success: true,
        data: messages
      };
    } catch (error) {
      const errorData = {
        error: error.message,
        error_type: error.constructor.name,
        stack: error.stack,
        name: error.name
      };
      console.error('❌ ADMIN API - getMessages - Erreur:', JSON.stringify(errorData, null, 2));
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des messages'
      };
    }
  }

  async sendMessage(messageData) {
    try {
      this._ensureToken();
      
      // NOUVEAU SCHÉMA: Validation obligatoire
      // recipient_type peut être 'all', 'specific', ou 'admin'
      if (messageData.recipient_type !== 'all' && messageData.recipient_type !== 'specific' && messageData.recipient_type !== 'admin') {
        throw new Error('recipient_type doit être "all", "specific" ou "admin"');
      }
      
      // Si recipient_type = 'specific', recipient_ids est OBLIGATOIRE
      if (messageData.recipient_type === 'specific') {
        if (!messageData.recipient_ids || !Array.isArray(messageData.recipient_ids) || messageData.recipient_ids.length === 0) {
          throw new Error('recipient_ids est obligatoire lorsque recipient_type = "specific"');
        }
      }
      // Pour 'admin', recipient_ids est optionnel (vide = uniquement admin, non vide = admin + IDs supplémentaires)
      // Pour 'all', recipient_ids n'est pas pris en compte
      
      // Préparer les données pour l'envoi (supprimer sent_date et sent_time qui n'existent plus)
      const backendData = {
        sender_id: messageData.sender_id,
        sender_name: messageData.sender_name,
        recipient_type: messageData.recipient_type, // 'all', 'specific', ou 'admin'
        subject: messageData.subject,
        content: messageData.content,
        priority: messageData.priority || 'normal'
      };
      
      // Ajouter student_id si fourni (optionnel)
      if (messageData.student_id !== undefined && messageData.student_id !== null) {
        backendData.student_id = parseInt(messageData.student_id);
      }
      
      // Ajouter recipient_ids selon le type
      if (messageData.recipient_type === 'specific') {
        // Pour 'specific', recipient_ids est obligatoire
        backendData.recipient_ids = messageData.recipient_ids.map(id => parseInt(id)).filter(id => id > 0);
      } else if (messageData.recipient_type === 'admin') {
        // Pour 'admin', recipient_ids est optionnel
        // Si fourni et non vide, tous les admins + ces IDs recevront le message
        // Si vide/null, seuls les admins recevront le message
        if (messageData.recipient_ids && Array.isArray(messageData.recipient_ids) && messageData.recipient_ids.length > 0) {
          backendData.recipient_ids = messageData.recipient_ids.map(id => parseInt(id)).filter(id => id > 0);
        }
        // Si recipient_ids est vide, ne pas l'inclure (sera NULL en base)
      }
      // Pour 'all', ne pas inclure recipient_ids (n'est pas pris en compte)
      
      const url = `${this.baseUrl}/api/admin/messages`;
      const headers = {
        'Content-Type': 'application/json',
        ...(this.token && { 'Authorization': `Bearer ${this.token}` })
      };
      
      // Logs des paramètres en un seul objet JSON
      const requestParams = {
        method: 'POST',
        route: '/api/admin/messages',
        url: url,
        headers: headers,
        token_present: !!this.token,
        token_preview: this.token ? this.token.substring(0, 20) + '...' : null,
        base_url: this.baseUrl,
        parameters: {
          ...backendData,
          content_preview: backendData.content?.substring(0, 100) + (backendData.content?.length > 100 ? '...' : ''),
          content_length: backendData.content?.length || 0,
          recipient_ids_count: backendData.recipient_ids?.length || 0
        }
      };
      console.log('📤 ADMIN API - sendMessage - Requête:', JSON.stringify(requestParams, null, 2));
      
      const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(backendData)
      });

      const data = await response.json();
      
      // Logs de la réponse en un seul objet JSON
      const responseData = {
        status: response.status,
        status_text: response.statusText,
        response_ok: response.ok,
        response_body: data,
        message_id: data?.data?.id || null,
        recipients_count: data?.data?.recipients_count || null,
        success: data?.success || false
      };
      console.log('📤 ADMIN API - sendMessage - Réponse:', JSON.stringify(responseData, null, 2));

      if (!response.ok) {
        const errorData = {
          status: response.status,
          status_text: response.statusText,
          error_message: data?.message || data?.error || `HTTP error! status: ${response.status}`,
          response_body: data,
          validation_error: response.status === 400 ? true : false
        };
        console.error('❌ ADMIN API - sendMessage - Erreur HTTP:', JSON.stringify(errorData, null, 2));
        
        // Retourner l'erreur avec le message du backend pour affichage dans l'UI
        return {
          success: false,
          error: data?.message || data?.error || `HTTP error! status: ${response.status}`,
          status: response.status
        };
      }

      // Vérifier aussi si data.success est false (certaines erreurs peuvent retourner 200 avec success: false)
      if (data.success === false) {
        const errorData = {
          success: false,
          error_message: data?.message || data?.error || 'Erreur lors de l\'envoi du message',
          response_body: data
        };
        console.error('❌ ADMIN API - sendMessage - Erreur dans la réponse:', JSON.stringify(errorData, null, 2));
        return {
          success: false,
          error: data?.message || data?.error || 'Erreur lors de l\'envoi du message'
        };
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      const errorData = {
        error: error.message,
        error_type: error.constructor.name,
        stack: error.stack,
        name: error.name
      };
      console.error('❌ ADMIN API - sendMessage - Erreur:', JSON.stringify(errorData, null, 2));
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de l\'envoi du message'
      };
    }
  }

  // ==================== UTILITY METHODS ====================

  // Test de connexion
  async testConnection() {
    try {
      console.log('🔍 ApiService - testConnection - Début');
      console.log('🔍 ApiService - testConnection - baseUrl:', this.baseUrl);
      
      const response = await fetch(`${this.baseUrl}/api/test`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      console.log('🔍 ApiService - testConnection - Status:', response.status);
      console.log('🔍 ApiService - testConnection - Response OK:', response.ok);

      const data = await response.json();
      console.log('🔍 ApiService - testConnection - Response JSON:', data);

      return {
        success: response.ok,
        status: response.status,
        data: data
      };
    } catch (error) {
      console.error('❌ ApiService - testConnection - Erreur:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Méthode générique pour les requêtes
  async makeRequest(endpoint, options = {}) {
    try {
      this._ensureToken();
      
      const headers = {
          'Content-Type': 'application/json',
      };

      if (this.token) {
        headers['Authorization'] = `Bearer ${this.token}`;
      }

      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'GET',
        headers,
        ...options,
      });

      const data = await response.json();

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la requête'
      };
    }
  }

  // ==================== QURAN RECORDS ====================
  
  // Récupérer tous les enregistrements Coran (pour AdminDashboard)
  async getAllQuranRecords() {
    try {
      this._ensureToken();
      
      const url = `${this.baseUrl}/api/admin/quran-records`;
      const headers = {
        'Content-Type': 'application/json',
        ...(this.token && { 'Authorization': `Bearer ${this.token}` })
      };
      
      const response = await fetch(url, {
        method: 'GET',
        headers: headers
      });

      const data = await response.json();

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      console.error('❌ ADMIN API - getAllQuranRecords - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des سجلات القرآن'
      };
    }
  }

  // Ajouter un nouvel enregistrement Coran
  async addQuranRecord(recordData) {
    try {
      this._ensureToken();
      
      // Obtenir la date/heure actuelle au format DATETIME (YYYY-MM-DD HH:mm:ss)
      const now = new Date();
      const modifiedDate = now.toISOString().slice(0, 19).replace('T', ' '); // Format: "YYYY-MM-DD HH:mm:ss"
      
      // Mapper les données frontend (camelCase) vers DTO backend (camelCase)
      // Le backend attend maintenant des champs DTO en camelCase
      const backendData = {
        studentId: parseInt(recordData.studentId) || 0,
        teacherId: parseInt(recordData.teacherId) || 0,
        totalHizb: parseFloat(recordData.totalHizb) || 0,
        startPoint: recordData.startPoint || 'الناس',
        note: recordData.note || null,
        modifiedDate: modifiedDate,
        
        // Évaluations (OPTIONNELLES - ne pas inclure si vides/null/undefined)
        // Le backend utilisera 'NotDone' par défaut si non fourni
        ...(recordData.evalNewHifz && typeof recordData.evalNewHifz === 'string' && recordData.evalNewHifz.trim() !== '' 
          ? { evalNewHifz: recordData.evalNewHifz.trim() } 
          : {}),
        ...(recordData.evalOldHifzMurajah && typeof recordData.evalOldHifzMurajah === 'string' && recordData.evalOldHifzMurajah.trim() !== '' 
          ? { evalOldHifzMurajah: recordData.evalOldHifzMurajah.trim() } 
          : {}),
        ...(recordData.evalNewHifzMurajah && typeof recordData.evalNewHifzMurajah === 'string' && recordData.evalNewHifzMurajah.trim() !== '' 
          ? { evalNewHifzMurajah: recordData.evalNewHifzMurajah.trim() } 
          : {}),
        
        // ✅ repeatNewHifz : valeur de "الجلسة القادمة" (quran_next_session) à sauvegarder dans "آخر جلسة" (quran_records)
        repeatNewHifz: (recordData.repeatNewHifz !== undefined && recordData.repeatNewHifz !== null) 
          ? parseInt(recordData.repeatNewHifz) 
          : 0,
        
        // Nouveau Hifz
        newHifzTxt: recordData.newHifzTxt || null,
        newHifzRange: recordData.newHifzRange || null,
        
        // Ancien Hifz - Murajaah
        oldHifzMurajahTxt: recordData.oldHifzMurajahTxt || null,
        oldHifzMurajahRange: recordData.oldHifzMurajahRange || null,
        
        // Nouveau Hifz - Murajaah (NOUVEAU)
        newHifzMurajahText: recordData.newHifzMurajahText || null,
        newHifzMurajahRange: recordData.newHifzMurajahRange || null
        
        // ❌ SUPPRIMÉ : Les champs nextHalaqa* sont maintenant gérés dans quran_next_session
        // Utiliser createOrUpdateNextSession() séparément pour créer/mettre à jour la session suivante
      };
      
      const url = `${this.baseUrl}/api/admin/quran-records`;
      const headers = {
        'Content-Type': 'application/json',
        ...(this.token && { 'Authorization': `Bearer ${this.token}` })
      };
      
      console.log('🔍 ADMIN API - addQuranRecord - URL complète:', url);
      console.log('🔍 ADMIN API - addQuranRecord - Données backend (body):', JSON.stringify(backendData, null, 2));
      
      const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(backendData)
      });

      const data = await response.json();

      console.log('🔍 ADMIN API - addQuranRecord - Status:', response.status);

      if (!response.ok) {
        let errorMessage = data.message || data.error || `HTTP error! status: ${response.status}`;
        
        // Gérer spécifiquement l'erreur de contrainte unique (date dupliquée)
        const errorString = JSON.stringify(data).toLowerCase();
        if (errorString.includes('duplicate entry') || 
            errorString.includes('unique_student_date') ||
            (data.error && (data.error.includes('Duplicate entry') || data.error.includes('unique_student_date'))) ||
            (data.sqlMessage && data.sqlMessage.includes('Duplicate entry'))) {
          errorMessage = 'يوجد بالفعل جلسة لهذا الطالب في هذا التاريخ. يرجى تعديل الجلسة الموجودة بدلاً من إنشاء جلسة جديدة.';
        }
        
        throw new Error(errorMessage);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      console.error('❌ ADMIN API - addQuranRecord - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de l\'ajout du سجل القرآن'
      };
    }
  }

  // Récupérer un enregistrement Coran par ID
  async getQuranRecordById(recordId) {
    try {
      this._ensureToken();
      
      const url = `${this.baseUrl}/api/admin/quran-records/${recordId}`;
      const headers = {
        'Content-Type': 'application/json',
        ...(this.token && { 'Authorization': `Bearer ${this.token}` })
      };
      
      console.log('🔍 ADMIN API - getQuranRecordById - ID:', recordId);
      console.log('🔍 ADMIN API - getQuranRecordById - URL:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: headers
      });

      const data = await response.json();

      console.log('🔍 ADMIN API - getQuranRecordById - Status:', response.status);
      console.log('🔍 ADMIN API - getQuranRecordById - Response data:', JSON.stringify(data, null, 2));

      if (!response.ok) {
        throw new Error(data.message || data.error || `HTTP error! status: ${response.status}`);
      }

      // S'assurer que data.data existe, sinon utiliser data directement
      const recordData = data.data || data;
      console.log('🔍 ADMIN API - getQuranRecordById - Record data final:', JSON.stringify(recordData, null, 2));

      return {
        success: true,
        data: recordData
      };
    } catch (error) {
      console.error('❌ ADMIN API - getQuranRecordById - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération du سجل القرآن'
      };
    }
  }

  // Mettre à jour un enregistrement Coran
  async updateQuranRecord(recordId, recordData) {
    try {
      this._ensureToken();
      
      // ⚠️ Exclure explicitement repeatNewHifz - utiliser la route dédiée PATCH /api/quran-records/:id/repeat-new-hifz
      const { repeatNewHifz, ...dataWithoutRepeatNewHifz } = recordData;
      
      // Mapper les données frontend (camelCase) vers DTO backend (camelCase)
      const backendData = {
        studentId: parseInt(dataWithoutRepeatNewHifz.studentId) || 0,
        teacherId: parseInt(dataWithoutRepeatNewHifz.teacherId) || 0,
        totalHizb: parseFloat(dataWithoutRepeatNewHifz.totalHizb) || 0,
        startPoint: dataWithoutRepeatNewHifz.startPoint || 'الناس',
        note: dataWithoutRepeatNewHifz.note || null,
        modifiedDate: dataWithoutRepeatNewHifz.modifiedDate || new Date().toISOString().slice(0, 19).replace('T', ' '),
        
        // Évaluations (OPTIONNELLES - ne pas inclure si vides/null/undefined pour mise à jour partielle)
        // Le backend conservera les valeurs existantes si non fourni lors d'un UPDATE
        // Le backend utilisera 'NotDone' par défaut si non fourni lors d'un INSERT
        ...(dataWithoutRepeatNewHifz.evalNewHifz && typeof dataWithoutRepeatNewHifz.evalNewHifz === 'string' && dataWithoutRepeatNewHifz.evalNewHifz.trim() !== '' 
          ? { evalNewHifz: dataWithoutRepeatNewHifz.evalNewHifz.trim() } 
          : {}),
        ...(dataWithoutRepeatNewHifz.evalOldHifzMurajah && typeof dataWithoutRepeatNewHifz.evalOldHifzMurajah === 'string' && dataWithoutRepeatNewHifz.evalOldHifzMurajah.trim() !== '' 
          ? { evalOldHifzMurajah: dataWithoutRepeatNewHifz.evalOldHifzMurajah.trim() } 
          : {}),
        ...(dataWithoutRepeatNewHifz.evalNewHifzMurajah && typeof dataWithoutRepeatNewHifz.evalNewHifzMurajah === 'string' && dataWithoutRepeatNewHifz.evalNewHifzMurajah.trim() !== '' 
          ? { evalNewHifzMurajah: dataWithoutRepeatNewHifz.evalNewHifzMurajah.trim() } 
          : {}),
        
        // ❌ repeatNewHifz : NE PAS INCLURE (utiliser la route dédiée PATCH /api/quran-records/:id/repeat-new-hifz)
        // Ce champ est EXCLUS explicitement pour éviter l'erreur du backend
        
        // Nouveau Hifz (OPTIONNEL - ne pas inclure si non fourni pour mise à jour partielle)
        // Le backend exige new_hifz_pos si new_hifz est fourni, donc on n'inclut que si explicitement fourni
        ...(dataWithoutRepeatNewHifz.newHifzTxt !== undefined 
          ? { newHifzTxt: dataWithoutRepeatNewHifz.newHifzTxt || null } 
          : {}),
        ...(dataWithoutRepeatNewHifz.newHifzRange !== undefined 
          ? { newHifzRange: dataWithoutRepeatNewHifz.newHifzRange || null } 
          : {}),
        
        // Ancien Hifz - Murajaah (OPTIONNEL - ne pas inclure si non fourni pour mise à jour partielle)
        ...(dataWithoutRepeatNewHifz.oldHifzMurajahTxt !== undefined 
          ? { oldHifzMurajahTxt: dataWithoutRepeatNewHifz.oldHifzMurajahTxt || null } 
          : {}),
        ...(dataWithoutRepeatNewHifz.oldHifzMurajahRange !== undefined 
          ? { oldHifzMurajahRange: dataWithoutRepeatNewHifz.oldHifzMurajahRange || null } 
          : {}),
        
        // Nouveau Hifz - Murajaah (OPTIONNEL - ne pas inclure si non fourni pour mise à jour partielle)
        ...(dataWithoutRepeatNewHifz.newHifzMurajahText !== undefined 
          ? { newHifzMurajahText: dataWithoutRepeatNewHifz.newHifzMurajahText || null } 
          : {}),
        ...(dataWithoutRepeatNewHifz.newHifzMurajahRange !== undefined 
          ? { newHifzMurajahRange: dataWithoutRepeatNewHifz.newHifzMurajahRange || null } 
          : {})
        
        // ❌ SUPPRIMÉ : Les champs nextHalaqa* sont maintenant gérés dans quran_next_session
        // Utiliser updateNextSessionByStudent() ou createOrUpdateNextSession() séparément pour créer/mettre à jour la session suivante
      };
      
      console.log('🔍 ADMIN API - updateQuranRecord - ID:', recordId);
      console.log('🔍 ADMIN API - updateQuranRecord - Données envoyées:', backendData);
      console.log('🔍 ADMIN API - updateQuranRecord - Route:', `${this.baseUrl}/api/admin/quran-records/${recordId}`);
      
      const response = await fetch(`${this.baseUrl}/api/admin/quran-records/${recordId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        },
        body: JSON.stringify(backendData)
      });

      const data = await response.json();

      console.log('🔍 ADMIN API - updateQuranRecord - Status:', response.status);

      if (!response.ok) {
        throw new Error(data.message || data.error || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      console.error('❌ ADMIN API - updateQuranRecord - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la mise à jour du سجل القرآن'
      };
    }
  }

  // Mettre à jour uniquement repeatNewHifz d'un enregistrement Coran
  async updateRepeatNewHifz(recordId, repeatNewHifz) {
    this._ensureToken();
    return await ApiServiceShared.updateRepeatNewHifz(
      recordId, 
      repeatNewHifz, 
      this.token, 
      this._handleAuthError.bind(this)
    );
  }

  // Supprimer un enregistrement Coran
  async deleteQuranRecord(recordId) {
    try {
      this._ensureToken();
      
      console.log('🔍 ADMIN API - deleteQuranRecord - ID:', recordId);
      console.log('🔍 ADMIN API - deleteQuranRecord - Route:', `${this.baseUrl}/api/admin/quran-records/${recordId}`);
      
      const response = await fetch(`${this.baseUrl}/api/admin/quran-records/${recordId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      const data = await response.json();

      console.log('🔍 ADMIN API - deleteQuranRecord - Status:', response.status);

      if (!response.ok) {
        throw new Error(data.message || data.error || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      console.error('❌ ADMIN API - deleteQuranRecord - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la suppression du سجل القرآن'
      };
    }
  }

  // ==================== EMAILS ====================
  
  // Récupérer les emails envoyés
  async getSentEmails(params = {}) {
    try {
      this._ensureToken();
      
      const queryParams = new URLSearchParams();
      if (params.status) queryParams.append('status', params.status);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.offset) queryParams.append('offset', params.offset);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      if (params.to) queryParams.append('to', params.to);
      if (params.from) queryParams.append('from', params.from);
      
      const url = `${this.baseUrl}/api/admin/sended-emails${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      const data = await response.json();

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || [],
        pagination: data.pagination || {}
      };
    } catch (error) {
      console.error('❌ ADMIN API - getSentEmails - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des emails envoyés'
      };
    }
  }

  // Récupérer un email envoyé spécifique
  async getSentEmailById(emailId) {
    try {
      this._ensureToken();
      
      const url = `${this.baseUrl}/api/admin/sended-emails/${emailId}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      const data = await response.json();

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data
      };
    } catch (error) {
      console.error('❌ ADMIN API - getSentEmailById - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération de l\'email'
      };
    }
  }

  // Statistiques des emails envoyés
  async getSentEmailsStats() {
    try {
      this._ensureToken();
      
      const url = `${this.baseUrl}/api/admin/sended-emails/stats/summary`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      const data = await response.json();

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data
      };
    } catch (error) {
      console.error('❌ ADMIN API - getSentEmailsStats - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des statistiques'
      };
    }
  }

  // Récupérer les emails reçus
  async getReceivedEmails(params = {}) {
    try {
      this._ensureToken();
      
      const queryParams = new URLSearchParams();
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.offset) queryParams.append('offset', params.offset);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      if (params.from) queryParams.append('from', params.from);
      if (params.to) queryParams.append('to', params.to);
      if (params.subject) queryParams.append('subject', params.subject);
      
      const url = `${this.baseUrl}/api/admin/received-emails${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      const data = await response.json();

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || [],
        pagination: data.pagination || {}
      };
    } catch (error) {
      console.error('❌ ADMIN API - getReceivedEmails - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des emails reçus'
      };
    }
  }

  // Récupérer un email reçu spécifique
  async getReceivedEmailByResendId(resendId) {
    try {
      this._ensureToken();
      
      const url = `${this.baseUrl}/api/admin/received-emails/${resendId}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      const data = await response.json();

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data
      };
    } catch (error) {
      console.error('❌ ADMIN API - getReceivedEmailByResendId - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération de l\'email'
      };
    }
  }

  // Statistiques des emails reçus
  async getReceivedEmailsStats() {
    try {
      this._ensureToken();
      
      const url = `${this.baseUrl}/api/admin/received-emails/stats/summary`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      const data = await response.json();

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data
      };
    } catch (error) {
      console.error('❌ ADMIN API - getReceivedEmailsStats - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des statistiques'
      };
    }
  }

  // Synchroniser les emails reçus depuis Resend
  async syncReceivedEmails(limit = 50) {
    try {
      this._ensureToken();
      
      console.log(`🔄 Synchronisation des emails reçus depuis Resend (limit: ${limit})...`);
      
      const response = await fetch(`${this.baseUrl}/api/admin/received-emails/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        },
        body: JSON.stringify({ limit })
      });

      const data = await response.json();

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      console.log(`✅ Synchronisation terminée:`, data.data);

      return {
        success: true,
        data: data.data,
        message: data.message
      };
    } catch (error) {
      console.error('❌ ADMIN API - syncReceivedEmails - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la synchronisation des emails'
      };
    }
  }

  // Renvoyer les informations de connexion par email (route publique, sans authentification)
  async resendCredentials(userType, { email, username, phone, language = 'ar' }) {
    try {
      // Vérifier qu'au moins un critère est fourni
      if (!email && !username && !phone) {
        return {
          success: false,
          error: 'Au moins un des champs suivants doit être fourni: email, username, phone'
        };
      }

      const body = {
        user_type: userType,
        language: language
      };

      if (email) body.email = email;
      if (username) body.username = username;
      if (phone) body.phone = phone;

      // Utiliser la route publique (sans authentification)
      const url = `${this.baseUrl}/api/admin/resend-credentials/public`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (!response.ok) {
        // Vérifier si c'est une erreur d'authentification (401)
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        message: data.message,
        data: data.data
      };
    } catch (error) {
      console.error('❌ ADMIN API - resendCredentials - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors du renvoi des informations de connexion'
      };
    }
  }

  // ==================== SCHOOL YEARS ====================

  async getSchoolYears() {
    try {
      this._ensureToken();
      const response = await fetch(`${this.baseUrl}/api/admin/school-years`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { Authorization: `Bearer ${this.token}` }),
        },
      });
      const data = await response.json();
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token requis');
          return { success: false, error: 'Session expirée' };
        }
        throw new Error(data.message || `HTTP ${response.status}`);
      }
      return {
        success: true,
        data: data.data || [],
        currentSchoolYear: data.currentSchoolYear || null,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur années scolaires',
      };
    }
  }

  async createSchoolYear(payload = {}) {
    try {
      this._ensureToken();
      const response = await fetch(`${this.baseUrl}/api/admin/school-years`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { Authorization: `Bearer ${this.token}` }),
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || `HTTP ${response.status}`);
      }
      return { success: true, data: data.data, message: data.message };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur création année',
      };
    }
  }

  async closeSchoolYear(closeId, activateSchoolYearId) {
    try {
      this._ensureToken();
      const response = await fetch(
        `${this.baseUrl}/api/admin/school-years/${closeId}/close`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(this.token && { Authorization: `Bearer ${this.token}` }),
          },
          body: JSON.stringify({
            activate_school_year_id: activateSchoolYearId,
          }),
        }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || `HTTP ${response.status}`);
      }
      return {
        success: true,
        data: data.data,
        message: data.message,
        activated: data.data?.activated,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur clôture année',
      };
    }
  }

  async setInscriptionSchoolYear(yearId) {
    try {
      this._ensureToken();
      const response = await fetch(
        `${this.baseUrl}/api/admin/school-years/${yearId}/set-inscription`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(this.token && { Authorization: `Bearer ${this.token}` }),
          },
        }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || `HTTP ${response.status}`);
      return { success: true, data: data.data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erreur' };
    }
  }

  async setCurrentSchoolYear(yearId) {
    try {
      this._ensureToken();
      const response = await fetch(
        `${this.baseUrl}/api/admin/school-years/${yearId}/set-current`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(this.token && { Authorization: `Bearer ${this.token}` }),
          },
        }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || `HTTP ${response.status}`);
      return { success: true, data: data.data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erreur' };
    }
  }

  // ==================== MONTHLY CONTRIBUTIONS (رسوم الطلاب) ====================

  async getMonthlyContributions(filters = {}) {
    try {
      this._ensureToken();
      
      const queryParams = new URLSearchParams();
      if (filters.academic_year) queryParams.append('academic_year', filters.academic_year);
      if (filters.school_year_id) queryParams.append('school_year_id', filters.school_year_id);
      if (filters.student_id) queryParams.append('student_id', filters.student_id);
      if (filters.parent_id) queryParams.append('parent_id', filters.parent_id);
      if (filters.status) queryParams.append('status', filters.status);
      this._appendFinanceYearToSearchParams(queryParams);
      
      const url = `${this.baseUrl}/api/admin/monthly-contribution${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      
      console.log('🔍 API - getMonthlyContributions - URL:', url);
      console.log('🔍 API - getMonthlyContributions - Filters:', filters);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      console.log('🔍 API - getMonthlyContributions - Response status:', response.status);
      console.log('🔍 API - getMonthlyContributions - Response ok:', response.ok);

      let data;
      try {
        const text = await response.text();
        console.log('🔍 API - getMonthlyContributions - Response text:', text);
        data = text ? JSON.parse(text) : {};
      } catch (parseError) {
        console.error('❌ API - getMonthlyContributions - Erreur de parsing JSON:', parseError);
        return {
          success: false,
          error: `Erreur de parsing de la réponse: ${parseError.message}`
        };
      }

      if (!response.ok) {
        console.error('❌ API - getMonthlyContributions - Erreur HTTP:', response.status, data);
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        // Utiliser le message d'erreur détaillé du backend si disponible
        const errorMessage = data.error || data.message || `HTTP error! status: ${response.status}`;
        console.error('❌ API - getMonthlyContributions - Message d\'erreur:', errorMessage);
        return {
          success: false,
          error: errorMessage
        };
      }

      console.log('✅ API - getMonthlyContributions - Succès, données:', data);
      return {
        success: true,
        data: data.data || []
      };
    } catch (error) {
      console.error('❌ API - getMonthlyContributions - Erreur catch:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des contributions mensuelles'
      };
    }
  }

  async getMonthlyContributionById(id) {
    try {
      this._ensureToken();
      
      const response = await fetch(`${this.baseUrl}${this._withFinanceUrl(`/api/admin/monthly-contribution/${id}`)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération de la contribution'
      };
    }
  }

  async createMonthlyContribution(contributionData) {
    try {
      this._ensureToken();
      
      const response = await fetch(`${this.baseUrl}/api/admin/monthly-contribution`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        },
        body: JSON.stringify(this._withFinanceBody(contributionData))
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data,
        message: data.message
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la création de la contribution'
      };
    }
  }

  async updateMonthlyContribution(id, contributionData) {
    try {
      this._ensureToken();
      
      const response = await fetch(`${this.baseUrl}${this._withFinanceUrl(`/api/admin/monthly-contribution/${id}`)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        },
        body: JSON.stringify(this._withFinanceBody(contributionData))
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data,
        message: data.message
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la mise à jour de la contribution'
      };
    }
  }

  async deleteMonthlyContribution(id) {
    try {
      this._ensureToken();
      
      const response = await fetch(`${this.baseUrl}${this._withFinanceUrl(`/api/admin/monthly-contribution/${id}`)}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        message: data.message
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la suppression de la contribution'
      };
    }
  }

  // ==================== TEACHER SALARIES ====================

  async getTeacherSalaries(filters = {}) {
    try {
      this._ensureToken();
      
      const queryParams = new URLSearchParams();
      if (filters.academic_year) queryParams.append('academic_year', filters.academic_year);
      if (filters.teacher_id) queryParams.append('teacher_id', filters.teacher_id);
      if (filters.financial_record_id) queryParams.append('financial_record_id', filters.financial_record_id);
      this._appendFinanceYearToSearchParams(queryParams);
      
      const url = `${this.baseUrl}/api/admin/teacher-salaries${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      
      console.log('🔍 API - getTeacherSalaries - URL:', url);
      console.log('🔍 API - getTeacherSalaries - Filters:', filters);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || []
      };
    } catch (error) {
      console.error('❌ API - getTeacherSalaries - Erreur catch:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des salaires des enseignants'
      };
    }
  }

  async updateTeacherSalary(id, salaryData) {
    try {
      this._ensureToken();
      
      const response = await fetch(`${this.baseUrl}${this._withFinanceUrl(`/api/admin/teacher-salaries/${id}`)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        },
        body: JSON.stringify(this._withFinanceBody(salaryData))
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data,
        message: data.message
      };
    } catch (error) {
      console.error('❌ API - updateTeacherSalary - Erreur catch:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la mise à jour du salaire'
      };
    }
  }

  // ==================== FINANCIAL RECORDS ====================

  async getFinancialRecords(filters = {}) {
    try {
      this._ensureToken();
      
      const queryParams = new URLSearchParams();
      if (filters.category_name) queryParams.append('category_name', filters.category_name);
      if (filters.category_type) queryParams.append('category_type', filters.category_type);
      if (filters.academic_year) queryParams.append('academic_year', filters.academic_year);
      this._appendFinanceYearToSearchParams(queryParams);
      
      const url = `${this.baseUrl}/api/admin/financial-records${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      
      console.log('🔍 API - getFinancialRecords - URL:', url);
      console.log('🔍 API - getFinancialRecords - Filters:', filters);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        }
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data || []
      };
    } catch (error) {
      console.error('❌ API - getFinancialRecords - Erreur catch:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des enregistrements financiers'
      };
    }
  }

  async addFinancialRecord(recordData) {
    try {
      this._ensureToken();
      
      console.log('API - addFinancialRecord - URL:', `${this.baseUrl}/api/admin/financial-records`);
      console.log('API - addFinancialRecord - Methode: POST');
      console.log('API - addFinancialRecord - Donnees envoyees:', JSON.stringify(recordData, null, 2));
      
      const response = await fetch(`${this.baseUrl}/api/admin/financial-records`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        },
        body: JSON.stringify(this._withFinanceBody(recordData))
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data,
        message: data.message
      };
    } catch (error) {
      console.error('❌ API - addFinancialRecord - Erreur catch:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de l\'ajout de l\'enregistrement financier'
      };
    }
  }

  async updateFinancialRecord(id, recordData) {
    try {
      this._ensureToken();
      
      console.log('API - updateFinancialRecord - URL:', `${this.baseUrl}/api/admin/financial-records/${id}`);
      console.log('API - updateFinancialRecord - Methode: PUT');
      console.log('API - updateFinancialRecord - Donnees envoyees:', JSON.stringify(recordData, null, 2));
      
      const response = await fetch(`${this.baseUrl}${this._withFinanceUrl(`/api/admin/financial-records/${id}`)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { 'Authorization': `Bearer ${this.token}` })
        },
        body: JSON.stringify(this._withFinanceBody(recordData))
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data,
        message: data.message
      };
    } catch (error) {
      console.error('❌ API - updateFinancialRecord - Erreur catch:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la mise à jour de l\'enregistrement financier'
      };
    }
  }

  // ============================================
  // MÉTHODES POUR LES PARAMÈTRES
  // ============================================

  /**
   * Récupérer tous les paramètres
   * GET /api/admin/parameters
   */
  async getParameters() {
    try {
      this._ensureToken();
      debugLog('📥 ADMIN API - getParameters - Début de la requête');

      const response = await fetch(`${this.baseUrl}/api/admin/parameters`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      debugLog('📥 ADMIN API - getParameters - Réponse reçue');

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data,
        message: data.message
      };
    } catch (error) {
      console.error('❌ API - getParameters - Erreur catch:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des paramètres'
      };
    }
  }

  /**
   * Récupérer un paramètre par son nom
   * GET /api/admin/parameters/:name
   */
  async getParameterByName(name) {
    try {
      this._ensureToken();
      debugLog(`📥 ADMIN API - getParameterByName - Paramètre: ${name}`);

      const response = await fetch(`${this.baseUrl}/api/admin/parameters/${encodeURIComponent(name)}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      debugLog('📥 ADMIN API - getParameterByName - Réponse reçue');

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data,
        message: data.message
      };
    } catch (error) {
      console.error('❌ API - getParameterByName - Erreur catch:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération du paramètre'
      };
    }
  }

  /**
   * Créer un nouveau paramètre
   * POST /api/admin/parameters
   * @param {Object} parameterData - { name, value, description }
   */
  async createParameter(parameterData) {
    try {
      this._ensureToken();
      console.log('📤 ADMIN API - createParameter - Données:', parameterData);

      const response = await fetch(`${this.baseUrl}/api/admin/parameters`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(parameterData)
      });

      const data = await response.json();
      console.log('📤 ADMIN API - createParameter - Réponse:', data);

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data,
        message: data.message
      };
    } catch (error) {
      console.error('❌ API - createParameter - Erreur catch:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la création du paramètre'
      };
    }
  }

  /**
   * Mettre à jour un paramètre existant
   * PUT /api/admin/parameters/:name
   * @param {string} name - Nom du paramètre
   * @param {Object} parameterData - { value, description }
   */
  async updateParameter(name, parameterData) {
    try {
      this._ensureToken();
      console.log(`🔄 ADMIN API - updateParameter - Paramètre: ${name}, Données:`, parameterData);

      const response = await fetch(`${this.baseUrl}/api/admin/parameters/${encodeURIComponent(name)}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(parameterData)
      });

      const data = await response.json();
      console.log('🔄 ADMIN API - updateParameter - Réponse:', data);

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        data: data.data,
        message: data.message
      };
    } catch (error) {
      console.error('❌ API - updateParameter - Erreur catch:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la mise à jour du paramètre'
      };
    }
  }

  /**
   * Supprimer un paramètre
   * DELETE /api/admin/parameters/:name
   * @param {string} name - Nom du paramètre à supprimer
   */
  async deleteParameter(name) {
    try {
      this._ensureToken();
      console.log(`🗑️  ADMIN API - deleteParameter - Paramètre: ${name}`);

      const response = await fetch(`${this.baseUrl}/api/admin/parameters/${encodeURIComponent(name)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      console.log('🗑️  ADMIN API - deleteParameter - Réponse:', data);

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data.message || 'Token d\'accès requis');
          return {
            success: false,
            error: 'Token d\'authentification invalide. Redirection vers la page d\'accueil.'
          };
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return {
        success: true,
        message: data.message
      };
    } catch (error) {
      console.error('❌ API - deleteParameter - Erreur catch:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la suppression du paramètre'
      };
    }
  }

  // ==================== QURAN NEXT SESSION ====================
  
  // Récupérer toutes les sessions suivantes (admin)
  async getAllNextSessions() {
    try {
      this._ensureToken();
      
      const url = `${this.baseUrl}/api/quran-next-session`;
      const headers = {
        'Content-Type': 'application/json',
        ...(this.token && { 'Authorization': `Bearer ${this.token}` })
      };
      
      const response = await fetch(url, {
        method: 'GET',
        headers: headers
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data?.message || 'Token invalide');
          return {
            success: false,
            error: 'Token d\'authentification invalide'
          };
        }
        return {
          success: false,
          error: data?.message || data?.error || 'Erreur lors de la récupération des sessions suivantes'
        };
      }
      
      return {
        success: true,
        data: data.data || [],
        message: data.message
      };
    } catch (error) {
      console.error('❌ ADMIN API - getAllNextSessions - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des sessions suivantes'
      };
    }
  }
  
  // Récupérer la session suivante d'un étudiant
  async getNextSessionForStudent(studentId) {
    try {
      this._ensureToken();
      
      const url = `${this.baseUrl}/api/quran-next-session/student/${studentId}`;
      const headers = {
        'Content-Type': 'application/json',
        ...(this.token && { 'Authorization': `Bearer ${this.token}` })
      };
      
      const response = await fetch(url, {
        method: 'GET',
        headers: headers
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data?.message || 'Token invalide');
          return {
            success: false,
            error: 'Token d\'authentification invalide'
          };
        }
        return {
          success: false,
          error: data?.message || data?.error || 'Erreur lors de la récupération de la session suivante'
        };
      }
      
      return {
        success: true,
        data: data.data || null,
        message: data.message
      };
    } catch (error) {
      console.error('❌ ADMIN API - getNextSessionForStudent - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération de la session suivante'
      };
    }
  }
  
  // Récupérer toutes les sessions suivantes d'un enseignant
  async getNextSessionsByTeacher(teacherId) {
    try {
      this._ensureToken();
      
      const url = `${this.baseUrl}/api/quran-next-session/teacher/${teacherId}`;
      const headers = {
        'Content-Type': 'application/json',
        ...(this.token && { 'Authorization': `Bearer ${this.token}` })
      };
      
      const response = await fetch(url, {
        method: 'GET',
        headers: headers
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data?.message || 'Token invalide');
          return {
            success: false,
            error: 'Token d\'authentification invalide'
          };
        }
        return {
          success: false,
          error: data?.message || data?.error || 'Erreur lors de la récupération des sessions suivantes'
        };
      }
      
      return {
        success: true,
        data: data.data || [],
        message: data.message
      };
    } catch (error) {
      console.error('❌ ADMIN API - getNextSessionsByTeacher - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la récupération des sessions suivantes'
      };
    }
  }
  
  // Créer ou mettre à jour la session suivante d'un étudiant (upsert)
  async createOrUpdateNextSession(studentId, sessionData) {
    try {
      this._ensureToken();
      
      const url = `${this.baseUrl}/api/quran-next-session`;
      const headers = {
        'Content-Type': 'application/json',
        ...(this.token && { 'Authorization': `Bearer ${this.token}` })
      };
      
      const backendData = {
        studentId: parseInt(studentId),
        teacherId: sessionData.teacherId ? parseInt(sessionData.teacherId) : undefined,
        startPoint: sessionData.startPoint || 'الناس',
        nextHalaqaNewHifzText: sessionData.nextHalaqaNewHifzText || null,
        nextHalaqaNewHifzRange: sessionData.nextHalaqaNewHifzRange || null,
        nextHalaqaOldHifzMurajahText: sessionData.nextHalaqaOldHifzMurajahText || null,
        nextHalaqaOldHifzMurajahRange: sessionData.nextHalaqaOldHifzMurajahRange || null,
        nextHalaqaNewHifzMurajahText: sessionData.nextHalaqaNewHifzMurajahText || null,
        nextHalaqaNewHifzMurajahRange: sessionData.nextHalaqaNewHifzMurajahRange || null,
        repeatNewHifz: sessionData.repeatNewHifz !== undefined ? parseInt(sessionData.repeatNewHifz) : 0,
        modifiedDate: sessionData.modifiedDate || new Date().toISOString()
      };
      
      const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(backendData)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data?.message || 'Token invalide');
          return {
            success: false,
            error: 'Token d\'authentification invalide'
          };
        }
        return {
          success: false,
          error: data?.message || data?.error || 'Erreur lors de la création/mise à jour de la session suivante'
        };
      }
      
      return {
        success: true,
        data: data.data || data,
        message: data.message || 'Session suivante créée/mise à jour avec succès'
      };
    } catch (error) {
      console.error('❌ ADMIN API - createOrUpdateNextSession - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la création/mise à jour de la session suivante'
      };
    }
  }
  
  // Mettre à jour la session suivante d'un étudiant (PUT)
  async updateNextSessionByStudent(studentId, sessionData) {
    try {
      this._ensureToken();
      
      const url = `${this.baseUrl}/api/quran-next-session/student/${studentId}`;
      const headers = {
        'Content-Type': 'application/json',
        ...(this.token && { 'Authorization': `Bearer ${this.token}` })
      };
      
      const backendData = {
        teacherId: sessionData.teacherId ? parseInt(sessionData.teacherId) : undefined,
        startPoint: sessionData.startPoint || 'الناس',
        nextHalaqaNewHifzText: sessionData.nextHalaqaNewHifzText || null,
        nextHalaqaNewHifzRange: sessionData.nextHalaqaNewHifzRange || null,
        nextHalaqaOldHifzMurajahText: sessionData.nextHalaqaOldHifzMurajahText || null,
        nextHalaqaOldHifzMurajahRange: sessionData.nextHalaqaOldHifzMurajahRange || null,
        nextHalaqaNewHifzMurajahText: sessionData.nextHalaqaNewHifzMurajahText || null,
        nextHalaqaNewHifzMurajahRange: sessionData.nextHalaqaNewHifzMurajahRange || null,
        repeatNewHifz: sessionData.repeatNewHifz !== undefined ? parseInt(sessionData.repeatNewHifz) : 0,
        modifiedDate: sessionData.modifiedDate || new Date().toISOString()
      };
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: headers,
        body: JSON.stringify(backendData)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data?.message || 'Token invalide');
          return {
            success: false,
            error: 'Token d\'authentification invalide'
          };
        }
        return {
          success: false,
          error: data?.message || data?.error || 'Erreur lors de la mise à jour de la session suivante'
        };
      }
      
      return {
        success: true,
        data: data.data || data,
        message: data.message || 'Session suivante mise à jour avec succès'
      };
    } catch (error) {
      console.error('❌ ADMIN API - updateNextSessionByStudent - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la mise à jour de la session suivante'
      };
    }
  }
  
  // Supprimer la session suivante d'un étudiant
  async deleteNextSessionByStudent(studentId) {
    try {
      this._ensureToken();
      
      const url = `${this.baseUrl}/api/quran-next-session/student/${studentId}`;
      const headers = {
        'Content-Type': 'application/json',
        ...(this.token && { 'Authorization': `Bearer ${this.token}` })
      };
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: headers
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data?.message || 'Token invalide');
          return {
            success: false,
            error: 'Token d\'authentification invalide'
          };
        }
        return {
          success: false,
          error: data?.message || data?.error || 'Erreur lors de la suppression de la session suivante'
        };
      }
      
      return {
        success: true,
        data: data.data || data,
        message: data.message || 'Session suivante supprimée avec succès'
      };
    } catch (error) {
      console.error('❌ ADMIN API - deleteNextSessionByStudent - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la suppression de la session suivante'
      };
    }
  }
  
  // Supprimer une session suivante par ID (admin)
  async deleteNextSessionById(id) {
    try {
      this._ensureToken();
      
      const url = `${this.baseUrl}/api/quran-next-session/${id}`;
      const headers = {
        'Content-Type': 'application/json',
        ...(this.token && { 'Authorization': `Bearer ${this.token}` })
      };
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: headers
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          this._handleAuthError(response, data?.message || 'Token invalide');
          return {
            success: false,
            error: 'Token d\'authentification invalide'
          };
        }
        return {
          success: false,
          error: data?.message || data?.error || 'Erreur lors de la suppression de la session suivante'
        };
      }
      
      return {
        success: true,
        data: data.data || data,
        message: data.message || 'Session suivante supprimée avec succès'
      };
    } catch (error) {
      console.error('❌ ADMIN API - deleteNextSessionById - Erreur:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur lors de la suppression de la session suivante'
      };
    }
  }

  // ── Demandes d'inscription ────────────────────────────────────────────────

  async getInscriptionRequests(params = {}) {
    const qs = Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== '')
      .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
      .join('&');
    return this.makeRequest(`/api/admin/inscription-requests${qs ? '?' + qs : ''}`, { method: 'GET' });
  }

  async getInscriptionRequestById(id) {
    return this.makeRequest(`/api/admin/inscription-requests/${id}`, { method: 'GET' });
  }

  async updateInscriptionStatus(id, status, adminNotes) {
    return this.makeRequest(`/api/admin/inscription-requests/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status, admin_notes: adminNotes }),
    });
  }

  async provisionInscriptionStudents(inscriptionId) {
    return this.makeRequest(`/api/admin/inscription-requests/${inscriptionId}/provision-students`, { method: 'POST' });
  }

  async linkInscriptionUser(inscriptionId, userId) {
    return this.makeRequest(`/api/admin/inscription-requests/${inscriptionId}/link-user`, {
      method: 'PATCH',
      body: JSON.stringify({ user_id: userId }),
    });
  }

  async getInscriptionStats() {
    return this.makeRequest('/api/admin/inscription-requests/stats/counts', { method: 'GET' });
  }

  async getContactStats() {
    return this.makeRequest('/api/admin/contact-requests/stats/counts', { method: 'GET' });
  }

  // ── Messages de contact ───────────────────────────────────────────────────

  async getContactRequests(params = {}) {
    const qs = Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== '')
      .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
      .join('&');
    return this.makeRequest(`/api/admin/contact-requests${qs ? '?' + qs : ''}`, { method: 'GET' });
  }

  async getContactRequestById(id) {
    return this.makeRequest(`/api/admin/contact-requests/${id}`, { method: 'GET' });
  }

  async updateContactStatus(id, status, replyMessage) {
    return this.makeRequest(`/api/admin/contact-requests/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status, reply_message: replyMessage }),
    });
  }

  // ── Paramètres de l'association ──────────────────────────────────────────────

  async getAssociationParams() {
    return this.makeRequest('/api/admin/association', { method: 'GET' });
  }

  async updateAssociationParams(params) {
    return this.makeRequest('/api/admin/association', {
      method: 'PUT',
      body: JSON.stringify(params),
    });
  }

  // ── Sections de la page d'accueil (landing) ──────────────────────────────────

  async getLandingSections() {
    return this.makeRequest('/api/admin/landing-sections/admin', { method: 'GET' });
  }

  async createLandingSection(data) {
    return this.makeRequest('/api/admin/landing-sections/admin', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateLandingSection(id, data) {
    return this.makeRequest(`/api/admin/landing-sections/admin/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteLandingSection(id) {
    return this.makeRequest(`/api/admin/landing-sections/admin/${id}`, {
      method: 'DELETE',
    });
  }

  async uploadLandingImage(file) {
    const token = this._ensureToken();
    const formData = new FormData();
    formData.append('image', file);
    const response = await fetch(`${this.baseUrl}/api/admin/landing-sections/admin/upload`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Erreur upload image');
    }
    return response.json();
  }
}

export default ApiServiceAdmin;
